/**
 * Save/Load Manager
 *
 * Handles game save data using localStorage
 * Can be extended to support cloud saves
 */

import { ClassName } from '@shared/types';
import { getCurrentLocale } from '@locales/index';
import { getAudioManager } from '@client/audio/AudioManager';
import { debugLog, debugWarn, debugError } from '@shared/utils/Logger';

// Save data structure
export interface SaveData {
  version: number;          // Save format version
  timestamp: number;        // When saved
  playTime: number;         // Total play time in seconds
  characterName?: string;   // Display name for save slot (e.g. "Alp Lv.12")
  lastSaved?: string;       // ISO date string for human-readable display

  // Player data
  player: {
    className: ClassName;
    level: number;
    experience: number;
    skillPoints: number;

    // Stats
    hp: number;
    maxHp: number;
    mana: number;
    maxMana: number;

    // Position
    positionX: number;
    positionY: number;
  };

  // Inventory (supports both legacy array and new multi-tab format)
  inventory: {
    gold: number;
    items: SavedItem[] | SavedInventoryData;
    equipment: Record<string, SavedItem | null>;
    effectStones?: SavedEffectStone[];
    potionBelt?: SavedPotionBelt;
  };

  // Skills (supports both legacy and new custom skill system)
  skills: {
    // Legacy format
    learned?: string[];
    hotbar?: (string | null)[];
    levels?: Record<string, number>;
    // New custom skill format
    customSkills?: object[];
    // V2 skill format
    customSkillsV2?: object[];
    // Active aura IDs
    activeAuras?: string[];
    // Reserved mana percentage
    reservedMana?: number;
  };

  // Passives
  passives: {
    allocated: string[];
    points: number;
    selectedBranches?: string[];              // New: branch selections (max 2)
    attributeChoices?: Record<string, string>; // New: nodeId → 'strength'|'agility'|'spirit'
    masterySelections?: Record<string, string>;
    insertedJewels?: Record<string, unknown>;
  };

  // Quest state (serialized from QuestManager)
  quests?: {
    quests: { id: string; status: string; objectives: { id: string; current: number; completed: boolean }[] }[];
    activeQuests: string[];
    completedQuests: string[];
  };
  currentZone?: string;

  // Waypoint state
  waypoints?: { activatedWaypoints?: string[]; lastTown?: string };

  // Currency stash
  currency?: Record<string, number>;

  // Game progress
  progress: {
    totalKills: number;
    bossesDefeated: number;
  };

  // Settings
  settings: {
    masterVolume: number;
    musicVolume: number;
    sfxVolume: number;
    locale: string;
  };
}

// Simplified item data for saving
export interface SavedItem {
  baseId: string;
  rarity: string;
  name: string;
  level: number;
  slot?: string;
  // Legacy format: single combined mods array
  mods?: Array<{ type: string; value: number; tier?: number; calcType?: string }>;
  // New format: separate mod categories for correct alignment
  implicit?: Array<{ type: string; value: number; tier?: number; calcType?: string }>;
  prefixes?: Array<{ type: string; value: number; tier?: number; calcType?: string }>;
  suffixes?: Array<{ type: string; value: number; tier?: number; calcType?: string }>;
}

// Saved effect stone data
export interface SavedEffectStone {
  id: string;
  name: string;
  nameKey: string;
  type: string;       // 'od' | 'gok' | 'su' | 'karanlik'
  rarity: string;     // 'normal' | 'magic' | 'rare' | 'legendary'
  effect: {
    id: string;
    name: string;
    description: string;
    damageBonus?: number;
    areaBonus?: number;
    cooldownReduction?: number;
    manaCostReduction?: number;
    elementOverride?: string;
    chainCount?: number;
    lifesteal?: number;
    slow?: number;
    knockback?: number;
    dot?: { damage: number; duration: number };
  };
}

// Saved potion belt data
export interface SavedPotionBelt {
  slots: ({ potionType: string; count: number } | null)[];
  maxSlots: number;
}

// Multi-tab inventory data format
export interface SavedInventoryData {
  tabs: SavedItem[][];
  tabCount: number;
  currencies: Record<string, number>;
  gold: number;
}

// Save slot info (for save menu display)
export interface SaveSlotInfo {
  slot: number;
  isEmpty: boolean;
  playerLevel?: number;
  className?: ClassName;
  characterName?: string;   // e.g. "Alp Lv.12"
  playTime?: number;
  timestamp?: number;
  lastSaved?: string;       // ISO date string for display
}

const SAVE_KEY_PREFIX = 'arpg_save_';
const AUTOSAVE_KEY = 'arpg_autosave';
const AUTOSAVE_BACKUP_KEY = 'arpg_autosave_backup';
const SETTINGS_KEY = 'arpg_settings';
const CUSTOM_MAPS_KEY = 'arpg_custom_maps';
const MAX_SAVE_SLOTS = 5;
const CURRENT_SAVE_VERSION = 2;

// Valid class names for validation
const VALID_CLASS_NAMES: readonly string[] = ['alp', 'kam', 'mergen', 'boru'];

// Custom map save data
export interface SavedMapData {
  id: string;
  name: string;
  timestamp: number;
  width: number;
  height: number;
  biome: string;
  tiles: number[][];
  props: Array<{
    type: string;
    x: number;
    y: number;
    variant?: number;
  }>;
  spawns: Array<{
    type: string;
    x: number;
    y: number;
    difficulty?: number;
  }>;
}

// Map list info for UI
export interface MapListInfo {
  id: string;
  name: string;
  timestamp: number;
  width: number;
  height: number;
  biome: string;
}

export class SaveManager {
  private static instance: SaveManager;
  private currentSlot: number = 0;
  private playTimeStart: number = Date.now();
  private totalPlayTime: number = 0;

  private constructor() {
    // Load settings on init and apply to AudioManager
    const settings = this.loadSettings();
    if (settings) {
      const audio = getAudioManager();
      audio.setMasterVolume(settings.masterVolume);
      audio.setMusicVolume(settings.musicVolume);
      audio.setSfxVolume(settings.sfxVolume);
    }
  }

  static getInstance(): SaveManager {
    if (!SaveManager.instance) {
      SaveManager.instance = new SaveManager();
    }
    return SaveManager.instance;
  }

  /**
   * Get info about all save slots
   */
  getSaveSlots(): SaveSlotInfo[] {
    const slots: SaveSlotInfo[] = [];

    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      const key = SAVE_KEY_PREFIX + i;
      const data = localStorage.getItem(key);

      if (data) {
        const parsed = this.safeParse(data, `save slot ${i}`);
        if (parsed && parsed.player) {
          slots.push({
            slot: i,
            isEmpty: false,
            playerLevel: parsed.player.level,
            className: parsed.player.className,
            characterName: parsed.characterName || this.buildCharacterName(parsed),
            playTime: parsed.playTime,
            timestamp: parsed.timestamp,
            lastSaved: parsed.lastSaved || (parsed.timestamp ? new Date(parsed.timestamp).toISOString() : undefined),
          });
        } else {
          // Data exists but is corrupted
          debugWarn('SaveManager', ` Corrupted save data in slot ${i}, marking as empty`);
          slots.push({ slot: i, isEmpty: true });
        }
      } else {
        slots.push({ slot: i, isEmpty: true });
      }
    }

    return slots;
  }

  /**
   * Check if a slot has save data
   */
  hasSaveData(slot: number): boolean {
    const key = SAVE_KEY_PREFIX + slot;
    return localStorage.getItem(key) !== null;
  }

  /**
   * Create save data from current game state
   */
  createSaveData(gameData: {
    player: {
      className: ClassName;
      level: number;
      experience: number;
      skillPoints: number;
      hp: number;
      maxHp: number;
      mana: number;
      maxMana: number;
      positionX: number;
      positionY: number;
    };
    inventory: {
      gold: number;
      items: SavedItem[] | SavedInventoryData;
      equipment: Record<string, SavedItem | null>;
      effectStones?: SavedEffectStone[];
      potionBelt?: SavedPotionBelt;
    };
    skills: {
      // Legacy format
      learned?: string[];
      hotbar?: (string | null)[];
      levels?: Record<string, number>;
      // New custom skill format
      customSkills?: object[];
      // V2 skill format
      customSkillsV2?: object[];
      // Active aura IDs
      activeAuras?: string[];
      // Reserved mana percentage
      reservedMana?: number;
    };
    passives: {
      allocated: string[];
      points: number;
    };
    progress: {
      totalKills: number;
      bossesDefeated: number;
    };
    quests?: SaveData['quests'] | object;
    currentZone?: string;
    waypoints?: { activatedWaypoints?: string[]; lastTown?: string };
    currency?: Record<string, number>;
  }): SaveData {
    // Calculate current session play time
    const sessionTime = (Date.now() - this.playTimeStart) / 1000;
    const totalTime = this.totalPlayTime + sessionTime;

    const now = Date.now();
    const saveData: SaveData = {
      version: CURRENT_SAVE_VERSION,
      timestamp: now,
      playTime: totalTime,
      characterName: this.buildCharacterNameFromPlayerData(gameData.player.className, gameData.player.level),
      lastSaved: new Date(now).toISOString(),
      player: gameData.player,
      inventory: gameData.inventory,
      skills: gameData.skills,
      passives: gameData.passives,
      quests: gameData.quests as SaveData['quests'],
      currentZone: gameData.currentZone,
      waypoints: gameData.waypoints,
      currency: gameData.currency,
      progress: gameData.progress,
      settings: {
        masterVolume: getAudioManager().getMasterVolume(),
        musicVolume: getAudioManager().getMusicVolume(),
        sfxVolume: getAudioManager().getSfxVolume(),
        locale: getCurrentLocale(),
      },
    };
    return saveData;
  }

  /**
   * Save game to a slot
   */
  save(slot: number, saveData: SaveData): boolean {
    if (slot < 0 || slot >= MAX_SAVE_SLOTS) {
      debugError('SaveManager',` Invalid save slot: ${slot}`);
      return false;
    }

    try {
      const key = SAVE_KEY_PREFIX + slot;
      localStorage.setItem(key, JSON.stringify(saveData));
      this.currentSlot = slot;

      // Persist audio settings separately so they survive across sessions
      if (saveData.settings) {
        this.saveSettings(saveData.settings);
      }

      debugLog('SaveManager',` Game saved to slot ${slot}`);
      return true;
    } catch (e) {
      debugError('SaveManager',` Failed to save:`, e);
      return false;
    }
  }

  /**
   * Load game from a slot
   */
  load(slot: number): SaveData | null {
    if (slot < 0 || slot >= MAX_SAVE_SLOTS) {
      debugError('SaveManager',` Invalid save slot: ${slot}`);
      return null;
    }

    const key = SAVE_KEY_PREFIX + slot;
    const data = localStorage.getItem(key);

    if (!data) {
      debugLog('SaveManager',` No save data in slot ${slot}`);
      return null;
    }

    // Safe-parse with corruption recovery
    const saveData = this.safeParse(data, `slot ${slot}`);
    if (!saveData) {
      debugWarn('SaveManager', ` Corrupted save in slot ${slot} - JSON parse failed`);
      return null;
    }

    // Validate critical fields
    if (!this.validateSaveData(saveData)) {
      debugWarn('SaveManager', ` Save data in slot ${slot} failed validation (missing critical fields)`);
      return null;
    }

    // Version migration if needed
    if (!saveData.version || saveData.version < CURRENT_SAVE_VERSION) {
      this.migrateSaveData(saveData);
    }

    this.currentSlot = slot;
    this.totalPlayTime = saveData.playTime || 0;
    this.playTimeStart = Date.now();

    debugLog('SaveManager',` Game loaded from slot ${slot}`);
    return saveData;
  }

  /**
   * Delete save from a slot
   */
  deleteSave(slot: number): boolean {
    if (slot < 0 || slot >= MAX_SAVE_SLOTS) {
      return false;
    }

    try {
      const key = SAVE_KEY_PREFIX + slot;
      localStorage.removeItem(key);
      debugLog('SaveManager',` Deleted save in slot ${slot}`);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Quick save to current slot
   */
  quickSave(saveData: SaveData): boolean {
    return this.save(this.currentSlot, saveData);
  }

  /**
   * Quick load from current slot
   */
  quickLoad(): SaveData | null {
    return this.load(this.currentSlot);
  }

  /**
   * Get current save slot
   */
  getCurrentSlot(): number {
    return this.currentSlot;
  }

  /**
   * Get total play time in seconds
   */
  getPlayTime(): number {
    const sessionTime = (Date.now() - this.playTimeStart) / 1000;
    return this.totalPlayTime + sessionTime;
  }

  /**
   * Format play time as string (HH:MM:SS)
   */
  formatPlayTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Save settings (independent of game saves)
   */
  saveSettings(settings: {
    masterVolume: number;
    musicVolume: number;
    sfxVolume: number;
    locale: string;
  }): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      debugError('SaveManager',' Failed to save settings:', e);
    }
  }

  /**
   * Load settings
   */
  loadSettings(): {
    masterVolume: number;
    musicVolume: number;
    sfxVolume: number;
    locale: string;
  } | null {
    try {
      const data = localStorage.getItem(SETTINGS_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      debugError('SaveManager',' Failed to load settings:', e);
    }
    return null;
  }

  /**
   * Auto-save game (uses dedicated auto-save slot)
   */
  autoSave(saveData: SaveData): boolean {
    try {
      // Backup the previous auto-save before overwriting
      const previousAutoSave = localStorage.getItem(AUTOSAVE_KEY);
      if (previousAutoSave) {
        try {
          localStorage.setItem(AUTOSAVE_BACKUP_KEY, previousAutoSave);
        } catch (backupErr) {
          debugWarn('SaveManager', ' Failed to backup previous auto-save:', backupErr);
          // Continue with the save even if backup fails
        }
      }

      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(saveData));

      // Persist audio settings
      if (saveData.settings) {
        this.saveSettings(saveData.settings);
      }

      debugLog('SaveManager', ' Auto-save completed');
      return true;
    } catch (e) {
      debugError('SaveManager',' Auto-save failed:', e);
      return false;
    }
  }

  /**
   * Load auto-save
   */
  loadAutoSave(): SaveData | null {
    // Try primary auto-save first
    const primaryResult = this.tryLoadAutoSaveFromKey(AUTOSAVE_KEY);
    if (primaryResult) {
      debugLog('SaveManager', ' Auto-save loaded');
      return primaryResult;
    }

    // Primary failed or is corrupted - try backup
    debugWarn('SaveManager', ' Primary auto-save unavailable, attempting backup recovery');
    const backupResult = this.tryLoadAutoSaveFromKey(AUTOSAVE_BACKUP_KEY);
    if (backupResult) {
      debugWarn('SaveManager', ' Recovered from auto-save backup');
      return backupResult;
    }

    debugWarn('SaveManager', ' No valid auto-save or backup found');
    return null;
  }

  /**
   * Attempt to load and validate auto-save data from a specific localStorage key.
   */
  private tryLoadAutoSaveFromKey(key: string): SaveData | null {
    const data = localStorage.getItem(key);
    if (!data) {
      return null;
    }

    const saveData = this.safeParse(data, key);
    if (!saveData) {
      debugWarn('SaveManager', ` Corrupted data in ${key} - JSON parse failed`);
      return null;
    }

    // Validate save integrity
    if (!this.validateSaveData(saveData)) {
      debugWarn('SaveManager', ` Data in ${key} failed validation`);
      return null;
    }

    if (!saveData.version || saveData.version < CURRENT_SAVE_VERSION) {
      this.migrateSaveData(saveData);
    }

    this.totalPlayTime = saveData.playTime || 0;
    this.playTimeStart = Date.now();
    return saveData;
  }

  /**
   * Check if auto-save exists
   */
  hasAutoSave(): boolean {
    return localStorage.getItem(AUTOSAVE_KEY) !== null;
  }

  /**
   * Get auto-save info for display
   */
  getAutoSaveInfo(): SaveSlotInfo | null {
    const data = localStorage.getItem(AUTOSAVE_KEY);
    if (!data) return null;

    const save = this.safeParse(data, 'autosave info');
    if (!save || !save.player) {
      debugWarn('SaveManager', ' Corrupted auto-save detected while reading info');
      return null;
    }

    return {
      slot: -1, // Special slot for auto-save
      isEmpty: false,
      playerLevel: save.player.level,
      className: save.player.className,
      characterName: save.characterName || this.buildCharacterName(save),
      playTime: save.playTime,
      timestamp: save.timestamp,
      lastSaved: save.lastSaved || (save.timestamp ? new Date(save.timestamp).toISOString() : undefined),
    };
  }

  /**
   * Delete auto-save
   */
  deleteAutoSave(): boolean {
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
      localStorage.removeItem(AUTOSAVE_BACKUP_KEY);
      debugLog('SaveManager', ' Auto-save and backup deleted');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Export save data as downloadable JSON
   */
  exportSave(slot: number): string | null {
    const data = this.load(slot);
    if (!data) return null;

    return JSON.stringify(data, null, 2);
  }

  /**
   * Import save data from JSON string
   */
  importSave(slot: number, jsonData: string): boolean {
    const saveData = this.safeParse(jsonData, 'import');
    if (!saveData) {
      debugError('SaveManager', ' Failed to import save: corrupted JSON');
      return false;
    }

    // Validate save data
    if (!this.validateSaveData(saveData)) {
      debugError('SaveManager',' Invalid save data format');
      return false;
    }

    // Migrate if from older version
    if (!saveData.version || saveData.version < CURRENT_SAVE_VERSION) {
      this.migrateSaveData(saveData);
    }

    return this.save(slot, saveData);
  }

  /**
   * Validate essential fields exist on loaded save data.
   * Returns false if the save is corrupted beyond repair.
   * Recoverable fields are filled with defaults in-place.
   */
  private validateSaveData(data: SaveData): boolean {
    if (!data || typeof data !== 'object') return false;

    // --- Critical fields: reject if missing ---

    // player object must exist
    if (!data.player || typeof data.player !== 'object') {
      debugWarn('SaveManager', ' Validation failed: missing player object');
      return false;
    }

    // player.className must be a valid class
    if (!data.player.className || !VALID_CLASS_NAMES.includes(data.player.className)) {
      debugWarn('SaveManager', ` Validation failed: invalid className "${data.player.className}"`);
      return false;
    }

    // player.level must be a positive number
    if (typeof data.player.level !== 'number' || data.player.level < 1 || !isFinite(data.player.level)) {
      debugWarn('SaveManager', ` Validation failed: invalid level "${data.player.level}"`);
      return false;
    }

    // inventory must exist
    if (!data.inventory) {
      debugWarn('SaveManager', ' Validation failed: missing inventory');
      return false;
    }

    // --- Recoverable fields: fill defaults if missing ---

    // currentZone defaults to starting zone
    if (!data.currentZone || typeof data.currentZone !== 'string') {
      debugWarn('SaveManager', ' Validation: missing currentZone, defaulting to "otag_koyu"');
      data.currentZone = 'otag_koyu';
    }

    if (!data.progress) {
      data.progress = { totalKills: 0, bossesDefeated: 0 };
    }
    if (!data.passives) {
      data.passives = { allocated: [], points: 0, selectedBranches: [], attributeChoices: {} };
    }
    if (!data.passives.selectedBranches) {
      data.passives.selectedBranches = [];
    }
    if (!data.passives.attributeChoices) {
      data.passives.attributeChoices = {};
    }
    if (!data.skills) {
      data.skills = {};
    }
    if (!data.settings) {
      data.settings = { masterVolume: 0.7, musicVolume: 0.5, sfxVolume: 0.7, locale: 'tr' };
    }
    if (typeof data.playTime !== 'number' || !isFinite(data.playTime)) {
      data.playTime = 0;
    }
    return true;
  }

  /**
   * Migrate save data from older versions (sequential pipeline).
   * Each step migrates from version N to N+1.
   */
  private migrateSaveData(saveData: SaveData): void {
    debugLog('SaveManager', ` Migrating save from version ${saveData.version || 0} to ${CURRENT_SAVE_VERSION}`);

    // v0 -> v1: original format, add missing optional fields
    if (!saveData.version || saveData.version < 1) {
      if (!saveData.quests) saveData.quests = { quests: [], activeQuests: [], completedQuests: [] };
      if (!saveData.currentZone) saveData.currentZone = 'otag_koyu';
      if (!saveData.waypoints) saveData.waypoints = { activatedWaypoints: [], lastTown: 'otag_koyu' };
      if (saveData.skills && !saveData.skills.customSkillsV2) saveData.skills.customSkillsV2 = [];
      if (saveData.skills && !saveData.skills.activeAuras) saveData.skills.activeAuras = [];
      saveData.version = 1;
    }

    // v1 -> v2: add characterName, lastSaved metadata, reserved mana default
    if (saveData.version === 1) {
      if (!saveData.characterName) {
        saveData.characterName = this.buildCharacterName(saveData);
      }
      if (!saveData.lastSaved) {
        saveData.lastSaved = saveData.timestamp ? new Date(saveData.timestamp).toISOString() : new Date().toISOString();
      }
      if (saveData.skills && saveData.skills.reservedMana === undefined) {
        saveData.skills.reservedMana = 0;
      }
      if (!saveData.passives.masterySelections) {
        saveData.passives.masterySelections = {};
      }
      if (!saveData.passives.insertedJewels) {
        saveData.passives.insertedJewels = {};
      }
      saveData.version = 2;
    }

    // Future migrations:
    // if (saveData.version === 2) { ... saveData.version = 3; }
  }

  // ============== HELPER METHODS ==============

  /**
   * Safely parse JSON data. Returns null on failure instead of throwing.
   */
  private safeParse(data: string, context: string): SaveData | null {
    try {
      const parsed = JSON.parse(data);
      if (!parsed || typeof parsed !== 'object') {
        debugWarn('SaveManager', ` safeParse(${context}): parsed value is not an object`);
        return null;
      }
      return parsed as SaveData;
    } catch (e) {
      debugWarn('SaveManager', ` safeParse(${context}): JSON.parse failed -`, e);
      return null;
    }
  }

  /**
   * Build a human-readable character name from save data (e.g. "Alp Lv.12")
   */
  private buildCharacterName(save: SaveData): string {
    if (!save.player) return 'Unknown';
    return this.buildCharacterNameFromPlayerData(save.player.className, save.player.level);
  }

  /**
   * Build a human-readable character name from player fields.
   */
  private buildCharacterNameFromPlayerData(className: string, level: number): string {
    const displayName = className ? className.charAt(0).toUpperCase() + className.slice(1) : 'Unknown';
    return `${displayName} Lv.${level || 1}`;
  }

  /**
   * Check if localStorage is available
   */
  static isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  // ============== CUSTOM MAP SAVE/LOAD ==============

  /**
   * Get all custom maps storage
   */
  private getCustomMaps(): Record<string, SavedMapData> {
    try {
      const data = localStorage.getItem(CUSTOM_MAPS_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      debugError('SaveManager',' Failed to load custom maps:', e);
    }
    return {};
  }

  /**
   * Save all custom maps storage
   */
  private saveCustomMaps(maps: Record<string, SavedMapData>): boolean {
    try {
      localStorage.setItem(CUSTOM_MAPS_KEY, JSON.stringify(maps));
      return true;
    } catch (e) {
      debugError('SaveManager',' Failed to save custom maps:', e);
      return false;
    }
  }

  /**
   * Save a custom map
   */
  saveMap(mapData: SavedMapData): boolean {
    const maps = this.getCustomMaps();
    maps[mapData.id] = {
      ...mapData,
      timestamp: Date.now(),
    };

    if (this.saveCustomMaps(maps)) {
      debugLog('SaveManager',` Custom map saved: ${mapData.name} (${mapData.id})`);
      return true;
    }
    return false;
  }

  /**
   * Load a custom map by ID
   */
  loadMap(mapId: string): SavedMapData | null {
    const maps = this.getCustomMaps();
    const map = maps[mapId];

    if (map) {
      debugLog('SaveManager',` Custom map loaded: ${map.name}`);
      return map;
    }

    debugLog('SaveManager',` Custom map not found: ${mapId}`);
    return null;
  }

  /**
   * Delete a custom map
   */
  deleteMap(mapId: string): boolean {
    const maps = this.getCustomMaps();

    if (maps[mapId]) {
      delete maps[mapId];
      if (this.saveCustomMaps(maps)) {
        debugLog('SaveManager',` Custom map deleted: ${mapId}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Get list of all custom maps (for UI)
   */
  getMapList(): MapListInfo[] {
    const maps = this.getCustomMaps();
    const list: MapListInfo[] = [];

    for (const id in maps) {
      const map = maps[id];
      list.push({
        id: map.id,
        name: map.name,
        timestamp: map.timestamp,
        width: map.width,
        height: map.height,
        biome: map.biome,
      });
    }

    // Sort by timestamp (newest first)
    list.sort((a, b) => b.timestamp - a.timestamp);
    return list;
  }

  /**
   * Check if a map ID exists
   */
  hasMap(mapId: string): boolean {
    const maps = this.getCustomMaps();
    return mapId in maps;
  }

  /**
   * Generate a unique map ID
   */
  generateMapId(): string {
    return `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Export custom map as downloadable JSON
   */
  exportMap(mapId: string): string | null {
    const map = this.loadMap(mapId);
    if (!map) return null;

    return JSON.stringify(map, null, 2);
  }

  /**
   * Import custom map from JSON string
   */
  importMap(jsonData: string): SavedMapData | null {
    try {
      const mapData: SavedMapData = JSON.parse(jsonData);

      // Validate map data
      if (!mapData.name || !mapData.tiles || !mapData.width || !mapData.height) {
        debugError('SaveManager',' Invalid map data format');
        return null;
      }

      // Generate new ID to avoid conflicts
      mapData.id = this.generateMapId();
      mapData.timestamp = Date.now();

      if (this.saveMap(mapData)) {
        return mapData;
      }
    } catch (e) {
      debugError('SaveManager',' Failed to import map:', e);
    }
    return null;
  }
}

// Convenience function
export function getSaveManager(): SaveManager {
  return SaveManager.getInstance();
}
